<template>
    <v-card :elevation="10">

        <div v-if="call.is_sticky">
            <v-card-text v-if="loading" class="text-center">
                <v-icon >mdi-loading mdi-spin</v-icon>
            </v-card-text>

            <div v-else>
                <v-card-title>
                    <template v-if="otherUser">
                        {{ otherUser.name }}
                    </template>
                    <template v-else-if="otherCustomer">
                        {{ otherCustomer.firstname }} {{ otherCustomer.lastname }}
                        <small v-if="similarCustomers.length > 0">(+{{ similarCustomers.length }})</small>
                    </template>
                    <template v-else>
                        {{ other.phone_number }}
                    </template>
                    <v-spacer></v-spacer>
                    <v-btn @click="checklist" color="secondary" small>Checklist</v-btn>
                </v-card-title>

                <v-card-text v-if="otherCustomer && call.answered_time" class="pb-0">
                    <SelectCustomerDossier label="Kies een dossier"
                                           :customerId="otherCustomer.id"
                                           v-model="call.dossier_id"
                                           :disabled="loading"
                                           @input="updateCall" />
                    <b-textarea outlined
                                v-model="call.message"
                                @input="updateCall"
                                label="Notitie"
                                class="mt-2"
                                :disabled="loading"
                                auto-grow />

                    <b-alert type="info"  class="mt-2" dense v-if="savingState === 'init'">
                        Deze gegevens worden automatisch opgeslagen
                    </b-alert>
                    <b-alert type="warning" class="mt-2" icon="mdi-loading mdi-spin" dense v-if="savingState === 'converting'">
                        Opgehangen, Sluit het gesprek niet af!
                    </b-alert>
                    <b-alert type="warning"  class="mt-2" icon="mdi-loading mdi-spin" dense v-if="savingState === 'pushed'">
                        Updaten, Sluit het gesprek niet af!
                    </b-alert>
                    <b-alert type="warning"  class="mt-2" icon="mdi-loading mdi-spin" dense v-if="savingState === 'saving'">
                        Bezig met update...
                    </b-alert>
                    <b-alert type="success"  class="mt-2" dense v-if="savingState === 'saved'">
                        Opgeslagen! Je kunt het gesprek nu afsluiten.
                    </b-alert>
                </v-card-text>

                <v-card-text v-if="otherUser">
                    In gesprek met {{ otherUser.name }}
                </v-card-text>
                <v-card-text>
                    <v-row>
                        <v-col>
                            <v-btn v-if="otherCustomer" block color="primary" :to="'/customer/' + otherCustomer.id" target="_blank">
                                Open klantkaart <v-icon right>mdi-open-in-new</v-icon>
                            </v-btn>
                            <v-btn v-else disabled block>
                                Open klantkaart <v-icon right>mdi-open-in-new</v-icon>
                            </v-btn>
                        </v-col>
                        <v-col>
                            <v-btn v-if="call.dossier_id" block color="primary darken-2" :to="'/dossier/' + call.dossier_id" target="_blank">
                                Open dossier <v-icon right>mdi-open-in-new</v-icon>
                            </v-btn>
                            <v-btn v-else disabled block>
                                Kies een dossier <v-icon right>mdi-open-in-new</v-icon>
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-card-text>

                <v-card-text>
                    <v-row>
                        <v-col>
                            Beantwoord:<br/>
                            {{ call.answered_time | formattedTimeSec }}
                        </v-col>
                        <v-col class="text-right">
                            Eind:<br/>
                            {{ call.end_time | formattedTimeSec }}
                        </v-col>
                        <v-col v-if="call.transferring">
                            Doorverbonden:<br/>
                            <template v-if="transferringUser">
                                {{ transferringUser.name }}
                            </template>
                            <template v-else-if="transferringCustomer">
                                {{ transferringCustomer.firstname }} {{ transferringCustomer.lastname }}
                            </template>
                            <template v-else>
                                {{ call.transferring.phone_number }}
                            </template>
                        </v-col>
                    </v-row>
                </v-card-text>
            </div>
        </div>

        <v-toolbar @click="toggleSticky" dark color="pink" class="white--text" aria-controls>
            <v-icon v-if="call.end_time" key="endedCall">
                mdi-phone-off
            </v-icon>
            <v-icon v-else-if="call.answered_time" key="answeredCall">
                mdi-phone-{{ direction }}
            </v-icon>
            <v-icon v-else v-animation="{name: 'ringing', duration: 1, steps: 10}" key="ringingCall">
                mdi-phone-ring
            </v-icon>
            <v-icon v-if="call.is_sticky">
                mdi-chevron-down
            </v-icon>
            <v-icon v-else>
                mdi-chevron-up
            </v-icon>
            <v-toolbar-title class="text-subtitle-1 font-weight-bold ml-2">
                <template v-if="otherUser">
                    {{ otherUser.name }}
                </template>
                <template v-else-if="otherCustomer">
                    {{ otherCustomer.firstname }} {{ otherCustomer.lastname }}
                    <small v-if="similarCustomers.length > 0">(+{{ similarCustomers.length }})</small>
                </template>
                <template v-else>
                    {{ other.phone_number }}
                </template>
            </v-toolbar-title>

            <v-spacer></v-spacer>

            <v-btn v-if="savingState === 'saving'" disabled icon>
                <v-icon>mdi-loading mdi-spin</v-icon>
            </v-btn>
            <v-btn v-else @click="ignore" icon>
                <v-icon>mdi-close</v-icon>
            </v-btn>
        </v-toolbar>
    </v-card>
</template>

<script>

import BTextarea from "@/components/General/Form/BTextarea";
import SelectCustomerDossier from "@/components/General/Form/Select/SelectCustomerDossier";
import ActivePhoneCall from "@/models/PhoneCall/ActivePhoneCall";
import User from "@/models/User/User";
import PhoneCall from "@/models/PhoneCall/PhoneCall";
import Customer from "@/models/Customer/Customer";
import PushNotification from "@/models/User/PushNotification";
import BAlert from "@/components/General/Alert/BAlert.vue";

export default {
    components: {
        BAlert,
        BTextarea,
        SelectCustomerDossier,
    },
    props: {
        call: {
            type: ActivePhoneCall,
            required: true
        },
    },
    data() {
        return {
            loading: true,
            loadingTimeout: null,
            apiTimeout: null,
            savingState: 'init',
            open: false,
            other: null,
            otherUser: null,
            otherCustomer: null,
            transferringUser: null,
            transferringCustomer: null,
            similarCustomers: [],
            direction: null,
        }
    },
    watch: {
        'call.phone_call_id'() {
            this.saveInApi();
        }
    },
    computed: {
        shouldOnlyPushSocket() {
            return !this.call.end_time;
        },
        waitingForApiJob() {
            return this.call.end_time && !this.call.phone_call_id;
        },
        shouldPushApi() {
            return this.call.phone_call_id;
        }
    },
    methods: {
        checklist() {
            PushNotification.phoneChecklist();
        },
        toggleSticky() {
            this.$socket.emit('call:update', {
                reference: this.call.reference,
                user_id: User.loggedInUser().id,
                message: this.call.message,
                dossier_id: this.call.dossier_id,
                is_sticky: !this.call.is_sticky,
            });
        },
        ignore() {
            this.$socket.emit('call:delete', {
                user_id: User.loggedInUser().id,
                reference: this.call.reference,
            });
        },
        updateCall() {
            this.emitCall();

            if (this.shouldOnlyPushSocket) {
                this.savingState = 'pushed';
                return;
            }

            if (this.waitingForApiJob) {
                this.savingState = 'converting';
                return;
            }

            if (this.shouldPushApi) {
                this.saveInApi();
            }
        },
        async buildData() {
            let promises = [];
            this.other = this.call.from;
            this.direction = 'incoming';
            if (this.call.from.user_id === User.loggedInUser().id) {
                this.other = this.call.to;
                this.direction = 'outgoing';
            }

            if (this.other.user_id) {
                promises.push(this.setOtherUser(this.other.user_id));
            } else {
                promises.push(this.setOtherCustomer(this.other.phone_number));
            }

            if (this.call.transferring) {
                if (this.call.transferring.user_id) {
                    promises.push(this.setTransferringUser(this.call.transferring.user_id));
                } else {
                    promises.push(this.setTransferringCustomer(this.call.transferring.phone_number));
                }
            }

            return Promise.all(promises);
        },
        async emitCall() {
            clearTimeout(this.loadingTimeout);
            this.loadingTimeout = setTimeout(() => {
                return this.$socket.emit('call:update', {
                    reference: this.call.reference,
                    user_id: User.loggedInUser().id,
                    message: this.call.message,
                    dossier_id: this.call.dossier_id,
                    is_sticky: this.call.is_sticky,
                });
            }, 300);
        },
        async saveInApi() {
            this.savingState = 'saving';
            clearTimeout(this.apiTimeout);
            this.apiTimeout = setTimeout(() => {
                PhoneCall.api().updateById(this.call.phone_call_id, {
                    message: this.call.message,
                    dossier_id: this.call.dossier_id,
                    is_sticky: this.call.is_sticky,
                }).then(() => {
                    this.savingState = 'saved';
                });
            }, 300);
        },
        async setOtherUser(userId) {
            this.otherUser = await User.api().find(userId, false);
        },
        async setOtherCustomer(phoneNumber) {
            this.otherCustomer = await Customer.api().where('any_telephone', phoneNumber).first();
            const similarCustomers = await Customer.api().where('any_telephone', phoneNumber).get();
            this.similarCustomers = similarCustomers.filter(customer => customer.id !== this.otherCustomer.id);
        },
        async setTransferringUser(userId) {
            this.transferringUser = await User.api().find(userId, false);
        },
        async setTransferringCustomer(phoneNumber) {
            this.transferringCustomer = await Customer.api().where('any_telephone', phoneNumber).first();
        },
    },
    created() {
        this.buildData().then(() => {
            this.loading = false;
        });
    },
    beforeDestroy() {
        clearTimeout(this.loadingTimeout);
        clearTimeout(this.apiTimeout);
    }
}
</script>
